import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, Scissors, Store, CheckCircle, BarChart3, Shield, TrendingUp } from 'lucide-react';
import Logo from './Logo';
import Navigation from './Navigation';
import Footer from './Footer';
import { useLanguage } from '@/context/LanguageContext';

const DigitalMarketplaceLanding: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const customerFeatures = [
    {
      icon: <Scissors className="w-8 h-8 text-brand-blue" />,
      title: t('landing.customer.feature1.title'),
      description: t('landing.customer.feature1')
    },
    {
      icon: <Users className="w-8 h-8 text-green-600" />,
      title: t('landing.customer.feature2.title'),
      description: t('landing.customer.feature2')
    },
    {
      icon: <Shield className="w-8 h-8 text-red-600" />,
      title: t('landing.customer.feature3.title'),
      description: t('landing.customer.feature3')
    }
  ];

  const businessFeatures = [
    {
      icon: <Store className="w-8 h-8 text-purple-600" />,
      title: t('landing.business.feature1.title'),
      description: t('landing.business.feature1')
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-orange-600" />,
      title: t('landing.business.feature2.title'),
      description: t('landing.business.feature2')
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-brand-blue" />,
      title: t('landing.business.feature3.title'),
      description: t('landing.business.feature3')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <Logo size="3xl" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-brand-blue mb-6">
              {t('landing.hero.title')}
            </h1>
            <p className="text-2xl md:text-3xl font-semibold text-gray-700 mb-4">
              {t('landing.hero.tagline')}
            </p>
            <div className="text-xl text-gray-600 max-w-4xl mx-auto mb-4 leading-relaxed space-y-4">
              <p>
                {t('landing.hero.story.p1')}
              </p>
              <p>
                {t('landing.hero.story.p2')}
              </p>
              <p>
                {t('landing.hero.story.p3')}
              </p>
              <p>
                {t('landing.hero.story.p4')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Two Personas Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-brand-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Customer Persona */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl border border-green-200"
            >
              <div className="text-center mb-6">
                <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.customer.title')}</h3>
                <p className="text-green-600 font-semibold text-lg">{t('landing.customer.subtitle')}</p>
              </div>
              <div className="text-gray-700 mb-6 space-y-4">
                <p className="text-center">
                  {t('landing.customer.description')}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.customer.feature1')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.customer.feature2')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.customer.feature3')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.customer.feature4')}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/login?signup=true')}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
              >
                {t('landing.customer.button')}
              </button>
            </motion.div>

            {/* Business User Persona */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border border-blue-200"
            >
              <div className="text-center mb-6">
                <div className="bg-brand-blue w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Store className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.business.title')}</h3>
                <p className="text-brand-blue font-semibold text-lg">{t('landing.business.subtitle')}</p>
              </div>
              <div className="text-gray-700 mb-6 space-y-4">
                <p className="text-center">
                  {t('landing.business.description')}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.business.feature1')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.business.feature2')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.business.feature3')}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t('landing.business.feature4')}</span>
                  </div>
                </div>
                <p className="text-center text-sm italic">
                  {t('landing.business.closing')}
                </p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full bg-brand-blue text-white py-3 rounded-lg font-semibold hover:bg-brand-blue/90 transition-colors"
              >
                {t('landing.business.button')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inviting CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('landing.cta.title')}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {t('landing.cta.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalMarketplaceLanding;
